import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import {
  backendApi, BACKEND_URL,
  type DebugRun, type RunDetails, type RunEvent, type RunArtifact,
  type SystemInfo, type HealthResponse,
} from '@/lib/backend';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import {
  StatusBadge, JsonBlock, ErrorBox, Timestamp,
  ArtifactItem, PayloadSection,
} from '@/components/debug/DebugHelpers';
import { DebugNav } from '@/components/debug/DebugNav';
import {
  Play, RefreshCw, Server, Terminal, FileJson,
  ChevronDown, Loader2, Radio, Square, Package,
} from 'lucide-react';

/* ── Run summary card ── */

function RunSummary({ run }: { run: RunDetails }) {
  const items: [string, React.ReactNode][] = [
    ['Run ID', <code className="text-xs font-mono">{run.id}</code>],
    ['Module', <Badge variant="secondary">{run.module_type ?? '—'}</Badge>],
    ['Status', <StatusBadge status={run.status} />],
    ['Step', <span>{run.current_step ?? '—'}</span>],
    ['Progress', run.progress_pct != null ? <span>{run.progress_pct}%</span> : <span>—</span>],
    ['Created', <Timestamp value={run.created_at} />],
    ['Started', <Timestamp value={run.started_at} />],
    ['Completed', <Timestamp value={run.completed_at} />],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-sm">
      {items.map(([label, node]) => (
        <div key={label}>
          <p className="text-muted-foreground text-xs">{label}</p>
          <div>{node}</div>
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════ */
/*  Main page                                    */
/* ══════════════════════════════════════════════ */

export default function BackendDebug() {
  const { isAdmin, loading: authLoading } = useAdminAuth();
  const qc = useQueryClient();
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);

  /* ── dev-only queries ── */

  const health = useQuery<HealthResponse>({
    queryKey: ['backend-health'],
    queryFn: backendApi.health,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const system = useQuery<SystemInfo>({
    queryKey: ['backend-system'],
    queryFn: backendApi.debugSystem,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const runs = useQuery<DebugRun[]>({
    queryKey: ['backend-runs'],
    queryFn: backendApi.debugRuns,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  /* ── proper run queries ── */

  const runDetails = useQuery<RunDetails>({
    queryKey: ['run', selectedRunId],
    queryFn: () => backendApi.getRun(selectedRunId!),
    enabled: !!selectedRunId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const runEvents = useQuery<RunEvent[]>({
    queryKey: ['run-events', selectedRunId],
    queryFn: () => backendApi.getRunEvents(selectedRunId!),
    enabled: !!selectedRunId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const runArtifacts = useQuery<RunArtifact[]>({
    queryKey: ['run-artifacts', selectedRunId],
    queryFn: () => backendApi.getRunArtifacts(selectedRunId!),
    enabled: !!selectedRunId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  /* ── polling ── */

  const isTerminal =
    runDetails.data?.status === 'succeeded' || runDetails.data?.status === 'failed';

  useEffect(() => {
    if (!polling || !selectedRunId || isTerminal) return;
    const id = setInterval(() => {
      qc.invalidateQueries({ queryKey: ['run', selectedRunId] });
      qc.invalidateQueries({ queryKey: ['run-events', selectedRunId] });
      qc.invalidateQueries({ queryKey: ['run-artifacts', selectedRunId] });
    }, 2000);
    return () => clearInterval(id);
  }, [polling, selectedRunId, isTerminal, qc]);

  useEffect(() => {
    if (isTerminal && polling) setPolling(false);
  }, [isTerminal, polling]);

  /* ── execute mutation ── */

  const executeMutation = useMutation({
    mutationFn: (runId: string) => backendApi.executeRun(runId),
    onSuccess: (_data, runId) => {
      setSelectedRunId(runId);
      qc.invalidateQueries({ queryKey: ['backend-runs'] });
      qc.invalidateQueries({ queryKey: ['run', runId] });
      qc.invalidateQueries({ queryKey: ['run-events', runId] });
      qc.invalidateQueries({ queryKey: ['run-artifacts', runId] });
      setPolling(true);
    },
  });

  /* ── refresh helpers ── */

  const refreshAll = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['backend-health'] });
    qc.invalidateQueries({ queryKey: ['backend-system'] });
    qc.invalidateQueries({ queryKey: ['backend-runs'] });
  }, [qc]);

  const refreshSelected = useCallback(() => {
    if (!selectedRunId) return;
    qc.invalidateQueries({ queryKey: ['run', selectedRunId] });
    qc.invalidateQueries({ queryKey: ['run-events', selectedRunId] });
    qc.invalidateQueries({ queryKey: ['run-artifacts', selectedRunId] });
  }, [qc, selectedRunId]);

  /* ── auth guard ── */

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  /* ── render ── */

  return (
    <div className="min-h-screen bg-background text-foreground p-6 space-y-6 max-w-6xl mx-auto">
      {/* Nav */}
      <DebugNav />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Terminal className="w-6 h-6 text-primary" />
            Backend Debug Console
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Development tool — connects to{' '}
            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{BACKEND_URL}</code>
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refreshAll}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh All
        </Button>
      </div>

      {/* ── Diagnostics ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Connection Diagnostics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="grid grid-cols-[120px_1fr] gap-y-2 gap-x-3 items-start">
            <span className="text-muted-foreground">Backend URL</span>
            <code className="text-xs bg-muted px-1.5 py-0.5 rounded break-all">{BACKEND_URL}</code>

            <span className="text-muted-foreground">Health</span>
            <div>
              {health.isLoading && <span className="text-muted-foreground">Checking…</span>}
              {health.isError && <ErrorBox error={health.error} />}
              {health.data && <StatusBadge status={health.data.status} />}
            </div>

            <span className="text-muted-foreground">System</span>
            <div>
              {system.isLoading && <span className="text-muted-foreground">Loading…</span>}
              {system.isError && <ErrorBox error={system.error} />}
              {system.data && <span className="text-green-600 dark:text-green-400">✓ loaded</span>}
            </div>

            <span className="text-muted-foreground">Runs</span>
            <div>
              {runs.isLoading && <span className="text-muted-foreground">Loading…</span>}
              {runs.isError && <ErrorBox error={runs.error} />}
              {runs.data && (
                <span className="text-green-600 dark:text-green-400">
                  ✓ {runs.data.length} run(s)
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => qc.invalidateQueries({ queryKey: ['backend-health'] })}>
              <RefreshCw className="w-3 h-3 mr-1" /> Health
            </Button>
            <Button variant="outline" size="sm" onClick={() => qc.invalidateQueries({ queryKey: ['backend-system'] })}>
              <RefreshCw className="w-3 h-3 mr-1" /> System
            </Button>
            <Button variant="outline" size="sm" onClick={() => qc.invalidateQueries({ queryKey: ['backend-runs'] })}>
              <RefreshCw className="w-3 h-3 mr-1" /> Runs
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── System Info ── */}
      {system.data && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Server className="w-4 h-4" /> System Info
            </CardTitle>
          </CardHeader>
          <CardContent>
            <JsonBlock data={system.data} />
          </CardContent>
        </Card>
      )}

      {/* ── Runs List ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Available Runs</CardTitle>
          <CardDescription>Click a run to inspect, or execute it</CardDescription>
        </CardHeader>
        <CardContent>
          {runs.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {runs.isError && <ErrorBox error={runs.error} />}
          {runs.data && runs.data.length === 0 && (
            <p className="text-sm text-muted-foreground">No runs found</p>
          )}
          {runs.data && runs.data.length > 0 && (
            <div className="space-y-2">
              {runs.data.map((run) => (
                <div
                  key={run.id}
                  className={`flex items-center justify-between rounded-md border p-3 cursor-pointer transition-colors
                    ${selectedRunId === run.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'}`}
                  onClick={() => {
                    setSelectedRunId(run.id);
                    setPolling(false);
                  }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <code className="text-xs text-muted-foreground font-mono truncate">
                      {run.id}
                    </code>
                    {run.module_type && (
                      <Badge variant="secondary" className="text-xs">
                        {run.module_type}
                      </Badge>
                    )}
                    {run.status && <StatusBadge status={run.status} />}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={executeMutation.isPending}
                    onClick={(e) => {
                      e.stopPropagation();
                      executeMutation.mutate(run.id);
                    }}
                  >
                    {executeMutation.isPending && executeMutation.variables === run.id ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                    ) : (
                      <Play className="w-3.5 h-3.5 mr-1" />
                    )}
                    Execute
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Selected Run ── */}
      {selectedRunId && (
        <>
          {/* A. Run Summary + controls */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">A. Run Summary</CardTitle>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Switch checked={polling} onCheckedChange={setPolling} id="poll-toggle" />
                    <label
                      htmlFor="poll-toggle"
                      className="cursor-pointer flex items-center gap-1"
                    >
                      {polling ? (
                        <>
                          <Radio className="w-3 h-3 text-green-500 animate-pulse" /> Polling
                        </>
                      ) : (
                        <>
                          <Square className="w-3 h-3" /> Poll
                        </>
                      )}
                    </label>
                  </div>
                  <Button variant="outline" size="sm" onClick={refreshSelected}>
                    <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {runDetails.isLoading && (
                <p className="text-sm text-muted-foreground">Loading…</p>
              )}
              {runDetails.isError && <ErrorBox error={runDetails.error} />}
              {runDetails.data && <RunSummary run={runDetails.data} />}
            </CardContent>
          </Card>

          {/* B. Run Payloads */}
          {runDetails.data && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileJson className="w-4 h-4" /> B. Run Payloads
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                <PayloadSection label="input_payload" data={runDetails.data.input_payload} />
                <PayloadSection label="context_payload" data={runDetails.data.context_payload} />
                <PayloadSection label="config_payload" data={runDetails.data.config_payload} />
                <PayloadSection label="result_payload" data={runDetails.data.result_payload} />
                <PayloadSection label="metrics_payload" data={runDetails.data.metrics_payload} />
                <PayloadSection label="error_payload" data={runDetails.data.error_payload} />
              </CardContent>
            </Card>
          )}

          {/* C. Run Events */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">C. Run Events</CardTitle>
            </CardHeader>
            <CardContent>
              {runEvents.isLoading && (
                <p className="text-sm text-muted-foreground">Loading…</p>
              )}
              {runEvents.isError && <ErrorBox error={runEvents.error} />}
              {runEvents.data && runEvents.data.length === 0 && (
                <p className="text-sm text-muted-foreground">No events yet</p>
              )}
              {runEvents.data && runEvents.data.length > 0 && (
                <ScrollArea className="h-64">
                  <div className="space-y-2">
                    {runEvents.data.map((evt, i) => (
                      <div
                        key={evt.id ?? i}
                        className="border border-border rounded-md p-2 text-xs space-y-1"
                      >
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="text-xs">
                            {evt.event_type}
                          </Badge>
                          {evt.created_at && (
                            <span className="text-muted-foreground">
                              {new Date(evt.created_at).toLocaleTimeString()}
                            </span>
                          )}
                        </div>
                        <p className="text-foreground">{evt.message}</p>
                        {evt.data_payload && Object.keys(evt.data_payload).length > 0 && (
                          <JsonBlock data={evt.data_payload} />
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* D. Run Artifacts */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="w-4 h-4" /> D. Run Artifacts
              </CardTitle>
            </CardHeader>
            <CardContent>
              {runArtifacts.isLoading && (
                <p className="text-sm text-muted-foreground">Loading…</p>
              )}
              {runArtifacts.isError && <ErrorBox error={runArtifacts.error} />}
              {runArtifacts.data && runArtifacts.data.length === 0 && (
                <p className="text-sm text-muted-foreground">No artifacts</p>
              )}
              {runArtifacts.data && runArtifacts.data.length > 0 && (
                <div className="space-y-2">
                  {runArtifacts.data.map((art, i) => (
                    <ArtifactItem key={art.id ?? i} artifact={art} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Execution result / error */}
      {executeMutation.data && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Last Execution Result</CardTitle>
          </CardHeader>
          <CardContent>
            <JsonBlock data={executeMutation.data} />
          </CardContent>
        </Card>
      )}
      {executeMutation.isError && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <ErrorBox error={executeMutation.error} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
